import { Skeleton } from '@jq4race/ui';
import type { ReactNode } from 'react';

export interface RestrictedMetaProps {
  /** Same `loading` flag the page already uses for its own skeleton state. */
  loading: boolean;
  /** Same `accessDenied` flag that swaps a list's table for `AccessDeniedState`. */
  accessDenied: boolean;
  /** The page's normal "N items" markup — rendered only when not loading and not denied. */
  children: ReactNode;
}

/**
 * Wraps a header "N items" count so it degrades safely once the underlying
 * list request failed with a 403.
 *
 * Root cause (same incident as `isForbiddenError`/`AccessDeniedState`): once
 * a list load is forbidden, its collection state is empty — or stale,
 * whatever was loaded before the role changed mid-session — so a literal
 * `{items.length}` in the header would show "0 events" (or a wrong,
 * leftover count) right next to the "Access denied" panel. Neither number
 * is real data; it's the *absence* of data that never arrived.
 *
 * Render this around whatever markup the page normally shows for the count
 * (a `<span>`, a `<Trans>`, anything) so it collapses to a plain "—"
 * whenever `accessDenied` is true — regardless of what the page's actual
 * item count happens to be.
 */
export function RestrictedMeta({ loading, accessDenied, children }: RestrictedMetaProps) {
  if (loading) {
    return <Skeleton className="inline-block h-[14px] w-6 align-middle" />;
  }
  if (accessDenied) {
    return <span className="font-medium tabular-nums text-jq-charcoal">—</span>;
  }
  return <>{children}</>;
}

/**
 * Same guard as `RestrictedMeta`, for a bare inline count that has no
 * separate loading state of its own (e.g. a tab badge showing how many
 * members/invitations exist).
 */
export function restrictedCount(accessDenied: boolean, value: number): ReactNode {
  return accessDenied ? '—' : value;
}
